/**
 * Stripe Payment Integration Service - PCI DSS Compliant Architecture
 *
 * This service implements secure payment processing through Stripe with strict
 * PCI compliance requirements. All sensitive payment operations are proxied through
 * the backend to maintain security boundaries and prevent data exposure.
 *
 * PCI DSS Compliance Architecture:
 * - NO credit card numbers ever touch our servers (Stripe handles via tokens)
 * - Backend proxy pattern for all payment operations
 *   WHY: Prevents exposure of Stripe secret keys, enables server-side validation,
 *   audit logging, and webhook signature verification
 * - Client-side uses Stripe Elements (iframe isolation) for card input
 *   WHY: Card data goes directly to Stripe servers, never through our JavaScript
 * - PaymentIntent/SetupIntent pattern for SCA compliance
 *   WHY: Strong Customer Authentication required in EU (PSD2 regulation)
 *
 * Security Principles:
 * - NEVER log payment IDs, customer IDs, or price IDs (information disclosure)
 * - All amounts validated server-side (prevent price manipulation)
 * - Idempotency keys used to prevent duplicate charges
 * - Webhook signatures verified server-side (prevent fake events)
 * - Customer data encrypted at rest in Stripe's PCI-compliant infrastructure
 *
 * Payment Flow Patterns:
 * 1. One-time payments: PaymentIntent → Confirm → Webhook verification
 * 2. Subscriptions: SetupIntent → Save payment method → Create subscription
 * 3. Payment updates: SetupIntent with existing customer → Update default method
 *
 * Regulatory Compliance:
 * - PCI DSS Level 1 (Stripe's certification covers card handling)
 * - PSD2/SCA (European Strong Customer Authentication)
 * - GDPR (customer data deletion, data portability)
 * - SOC 2 Type II (Stripe's infrastructure compliance)
 *
 * @module apiStripe
 * @see {@link .claude/security-rules.md} - Security standards for payment handling
 * @see {@link docs/09_PAYMENT_SUBSCRIPTION_SYSTEM.md} - Payment system architecture
 * @see {@link docs/29_STRIPE_INTEGRATION.md} - Detailed Stripe integration patterns
 */

import { getData } from './apiFetch';
import type {
  PaymentIntentResponse,
  SetupIntentResponse,
  StripeCustomerResponse,
  PaymentMethodResponse,
  StripePublishableKeyResponse,
  ReconciliationResponse,
} from '../types/api';
import type {
  CancelPaymentIntentParams,
  UpdatePaymentIntentParams,
  CreatePaymentIntentParams,
  ConfirmPaymentParams,
  RetrievePaymentIntentParams,
  CreateSetupIntentParams,
  CreateStandaloneSetupIntentParams,
  ConfirmSetupIntentParams,
  GetSetupIntentParams,
  GetCustomerParams,
  UpdateCustomerParams,
  ListPaymentMethodsParams,
  RemovePaymentMethodParams,
  UpdateSubscriptionPaymentMethodParams,
  UpdateBillingPaymentMethodParams,
  GetPublishableKeyParams,
  CancelStripeSubscriptionParams,
  ReconPaymentParams,
  UpdateAutoRenewParams,
} from '../types/api/parameters';

/**
 * Cancels a PaymentIntent to prevent charge and release hold on customer's card.
 *
 * WHY cancel instead of refund:
 * - PaymentIntent may be authorized but not captured (hotel/rental pattern)
 * - Cancellation releases the hold immediately vs refund taking 5-10 days
 * - No fees charged to merchant for cancelled (uncaptured) payments
 * - Prevents accidental charges for abandoned carts
 *
 * Security considerations:
 * - Backend verifies the payment belongs to authenticated user
 * - Cancellation reason logged for dispute resolution and analytics
 * - Cannot cancel already captured payments (must refund instead)
 * - Audit trail maintained for compliance and fraud prevention
 *
 * @param {CancelPaymentIntentParams} params - Cancellation parameters
 * @param {string} [params.cancellation_reason='requested_by_customer'] - Stripe enum for analytics
 * @param {string} params.payment_id - Stripe PaymentIntent ID (pi_xxx format)
 * @returns {Promise<PaymentIntentResponse>} Cancelled PaymentIntent confirmation
 * @security Backend validates user owns this payment before cancelling
 */
export async function cancelPaymentIntent({
  cancellation_reason = 'requested_by_customer',
  payment_id,
}: CancelPaymentIntentParams): Promise<PaymentIntentResponse> {
  const requestBody = {
    // WHY track cancellation reason: Required for Stripe reporting,
    // helps identify UX issues (duplicate, abandoned, fraudulent)
    cancellation_reason,
    payment_id, // WHY not in URL: Prevents payment IDs in access logs
  };

  const config = {
    method: 'post' as const,
    path: 'stripe/cancel-payment-intent',
    body: requestBody,
  };

  const data = await getData<PaymentIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Updates PaymentIntent details before confirmation.
 *
 * WHY update payment intent:
 * - Cart changes before checkout completion
 * - Customer details correction
 * - Metadata updates for tracking
 * - Amount adjustments (e.g., applied discounts)
 *
 * Security considerations:
 * - Backend validates updated amounts against product catalog
 * - Customer template prevents prototype pollution
 * - Cannot update after payment confirmed/captured
 *
 * @param {UpdatePaymentIntentParams} params - Update parameters
 * @returns {Promise<PaymentIntentResponse>} Updated PaymentIntent
 * @security Backend validates amount changes against secure price catalog
 */
export async function updatePaymentIntent({
  customer,
  payment,
}: UpdatePaymentIntentParams): Promise<PaymentIntentResponse> {
  const customerTemplate = {
    name: '',
    given_name: '',
    family_name: '',
    address: {},
    email: '',
    phone: '',
    // metadata: {},
  };

  const customerObj: Record<string, unknown> = {};

  if (customer) {
    Object.keys(customerTemplate).forEach(function (key) {
      // or obj1.hasOwnProperty(key)
      customerObj[key] = (customer as Record<string, unknown>)[key];
    });

    // DEV-1907: Build full name from given_name + family_name for Stripe.
    // Previously set to null, which wiped billing name in Stripe dashboard.
    // Stripe's customer.name field expects a single string, not split fields.
    const givenName = (customer as Record<string, unknown>).given_name || '';
    const familyName = (customer as Record<string, unknown>).family_name || '';
    customerObj.name = [givenName, familyName].filter(Boolean).join(' ') || null;
  }

  const requestBody = {
    customer: {
      ...customerObj,
    },
    receipt_email: (customer as { email?: string })?.email,
    paymentIntentId: payment.id,
  };

  const config = {
    method: 'put' as const,
    path: 'stripe/update-payment-intent',
    body: requestBody,
  };

  const data = await getData<PaymentIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Creates a SetupIntent for saving payment methods without immediate charge.
 *
 * WHY SetupIntent instead of direct card save:
 * - PSD2/SCA compliance requires authentication even for card saves
 * - Enables 3D Secure verification without charging the card
 * - Allows off-session payments later (subscriptions, recurring)
 * - Creates reusable payment method for future charges
 *
 * Security architecture:
 * - No amount specified (prevents accidental charges)
 * - Requires explicit customer consent via Stripe Elements
 * - Backend attaches to authenticated customer only
 * - Client secret allows secure completion in browser
 *
 * Use cases:
 * - Adding payment method to account (no immediate charge)
 * - Setting up subscription payment method
 * - Updating default payment method
 * - Trial-to-paid conversion preparation
 *
 * @param {CreateSetupIntentParams} params - Setup parameters
 * @param {string} params.paymentID - Internal payment ID for correlation
 * @returns {Promise<SetupIntentResponse>} SetupIntent with client_secret for Elements
 * @security Client secret is single-use and expires after 24 hours
 */
export async function createSetupIntent({
  paymentID,
}: CreateSetupIntentParams): Promise<SetupIntentResponse> {
  const config = {
    method: 'post' as const,
    path: 'stripe/create-setup-intent',
    body: {
      paymentID, // WHY correlation ID: Links Stripe object to our internal records
    },
  };

  const data = await getData<SetupIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Creates a standalone SetupIntent for adding payment methods without payment context.
 *
 * WHY standalone endpoint needed:
 * - Account payment method management has NO payment/subscription context
 * - Existing createSetupIntent() requires paymentID (payment recovery scenarios)
 * - Users need ability to add payment methods proactively to their account
 * - Separates payment recovery flows from general payment method management
 *
 * Security architecture:
 * - Backend correlates with authenticated user's Stripe customer
 * - No paymentID required (standalone payment method management)
 * - Follows same PCI compliance patterns as existing endpoints
 * - Client secret single-use and expires after 24 hours
 *
 * Use cases:
 * - Adding payment method from account settings
 * - Proactive payment method updates (before expiry)
 * - Setting up first payment method on new accounts
 *
 * @param {CreateStandaloneSetupIntentParams} _params - No parameters (uses session)
 * @returns {Promise<SetupIntentResponse>} SetupIntent with client_secret for Stripe Elements
 * @security Backend attaches to authenticated user's customer only
 * @see {@link docs/BACKEND_REQUIREMENTS_ADD_PAYMENT_METHOD.md} - Backend implementation spec
 */
export async function createStandaloneSetupIntent(
  _params?: CreateStandaloneSetupIntentParams
): Promise<SetupIntentResponse> {
  const config = {
    method: 'post' as const,
    path: 'payment-methods', // Backend expects payment-methods without stripe/ prefix
    body: _params || {}, // WHY pass params: Backend needs optional subscriptionId and currency context
  };

  const data = await getData<SetupIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Confirms a SetupIntent after customer authentication (3D Secure).
 *
 * WHY separate confirmation step:
 * - 3D Secure may require additional authentication (bank app, SMS)
 * - Confirmation happens after customer completes authentication
 * - Backend validation ensures setup belongs to authenticated user
 * - Prevents unauthorized payment method additions
 *
 * Security flow:
 * 1. Create SetupIntent (server returns client secret)
 * 2. Stripe Elements handles card input and 3DS if needed
 * 3. Confirm SetupIntent (this function) after authentication
 * 4. Payment method saved and attached to customer
 *
 * PCI compliance notes:
 * - Card details never pass through our servers
 * - Only payment method ID (pm_xxx) stored in our database
 * - Stripe handles all sensitive card data and tokenization
 *
 * @param {ConfirmSetupIntentParams} params - Confirmation parameters
 * @param {string} params.paymentIntentId - Correlates with payment if applicable
 * @param {string} params.setupIntentId - Stripe SetupIntent ID (seti_xxx)
 * @returns {Promise<SetupIntentResponse>} Confirmed SetupIntent with payment method
 * @security Backend verifies ownership before confirmation
 */
export async function confirmSetupIntent({
  paymentIntentId,
  setupIntentId,
}: ConfirmSetupIntentParams): Promise<SetupIntentResponse> {
  const config = {
    method: 'post' as const,
    path: 'payment-methods/confirm', // Backend expects payment-methods without stripe/ prefix
    body: {
      paymentIntentId, // WHY both IDs: Links setup to potential future payment
      setupIntentId, // Actual Stripe object to confirm
    },
  };

  const data = await getData<SetupIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Retrieves SetupIntent details for status checking.
 *
 * WHY retrieve setup intent:
 * - Check status after 3D Secure redirect
 * - Verify successful payment method save
 * - Handle error states in UI
 * - Retry failed authentications
 *
 * @param {GetSetupIntentParams} params - Retrieval parameters
 * @returns {Promise<SetupIntentResponse>} SetupIntent with current status
 * @security Backend verifies ownership before returning details
 */
export async function getSetupIntent({
  paymentIntentId,
  setupIntentId,
}: GetSetupIntentParams): Promise<SetupIntentResponse> {
  const config = {
    method: 'post' as const,
    path: 'stripe/retrieve-setup-intent',
    body: {
      paymentIntentId,
      setupIntentId,
    },
  };

  const data = await getData<SetupIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Creates a PaymentIntent for immediate payment processing.
 *
 * WHY PaymentIntent architecture:
 * - SCA/PSD2 compliance for European payments (required by law)
 * - Handles complex payment flows (3D Secure, authentication)
 * - Supports multiple payment methods (cards, wallets, bank transfers)
 * - Enables status tracking through webhooks (pending → succeeded)
 *
 * Security critical points:
 * - Line items and amounts MUST be validated server-side
 *   WHY: Client-side amounts can be manipulated via DevTools
 * - Price calculation happens on backend using secure price IDs
 *   WHY: Prevents price manipulation attacks
 * - Customer data sanitized to prevent injection attacks
 * - Idempotency key generated server-side (prevents double charges)
 *
 * PCI DSS compliance:
 * - No card data in request (only payment method tokens)
 * - Amount validation against product catalog
 * - Audit log of all payment attempts
 * - Webhook signature verification for status updates
 *
 * @param {CreatePaymentIntentParams} params - Payment parameters
 * @param {Array} params.lineItems - Products/subscriptions being purchased
 * @param {string} params.customer - Customer ID
 * @returns {Promise<PaymentIntentResponse>} PaymentIntent with client_secret
 * @security CRITICAL: Never trust client-provided amounts
 */
export async function createPaymentIntent({
  lineItems,
  customer,
}: CreatePaymentIntentParams): Promise<PaymentIntentResponse> {
  // WHY template pattern: Ensures only expected fields sent to Stripe,
  // prevents injection of unexpected metadata or fields
  const customerTemplate = {
    name: '',
    given_name: '',
    family_name: '',
    address: {},
    email: '',
    phone: '',
    // metadata: {}, // WHY commented: Metadata could leak sensitive info
  };

  const customerObj: Record<string, unknown> = {};

  if (customer) {
    // WHY explicit field copying: Prevents prototype pollution attacks,
    // ensures only whitelisted fields are sent to payment processor
    Object.keys(customerTemplate).forEach(function (key) {
      // or obj1.hasOwnProperty(key)
      customerObj[key] = (customer as unknown as Record<string, unknown>)[key];
    });
  }

  const requestBody = {
    lineItems, // WHY not amounts: Backend calculates from secure price catalog
    customer: {
      ...customerObj,
    },
    receipt_email: (customer as unknown as { email?: string })?.email, // WHY duplicate: Stripe needs this at root level
  };

  const config = {
    method: 'post' as const,
    path: 'stripe/create-payment-intent',
    body: requestBody,
  };

  const data = await getData<PaymentIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Retrieves Stripe publishable key for client-side SDK initialization.
 *
 * WHY dynamic key retrieval vs hardcoding:
 * - Environment-specific keys (test/live) without rebuilding
 * - Key rotation possible without code changes
 * - Centralized key management for multi-tenant architecture
 * - Prevents accidental key exposure in source control
 *
 * Security notes:
 * - Publishable key is safe for client-side (designed for browser)
 * - Cannot perform charges, only tokenization
 * - Secret key NEVER sent to client (backend only)
 * - Different keys for test vs production environments
 *
 * @param {GetPublishableKeyParams} _params - No parameters (uses channel context)
 * @returns {Promise<StripePublishableKeyResponse>} Publishable key for Stripe.js initialization
 * @security Publishable key is public-safe, secret key must never be exposed
 */
export async function getPublishableKey(
  _params?: GetPublishableKeyParams
): Promise<StripePublishableKeyResponse> {
  const path = 'stripe/publishable-key';
  const data = await getData<StripePublishableKeyResponse>({ path });

  return data;
}

/**
 * Retrieves Stripe customer object for authenticated user.
 *
 * WHY customer object needed:
 * - Shows saved payment methods for quick checkout
 * - Displays billing address for tax calculation
 * - Links subscriptions to user account
 * - Enables customer portal access
 *
 * Security architecture:
 * - Customer ID never exposed to client (backend correlation)
 * - Only returns data for authenticated user's customer
 * - Payment method details partially masked (last 4 digits only)
 * - No sensitive fields (full card numbers) included
 *
 * @param {GetCustomerParams} _params - No parameters (uses session)
 * @returns {Promise<StripeCustomerResponse | null>} Stripe customer or null if not authenticated
 * @security Backend ensures customer belongs to authenticated user
 */
export async function getCustomer(_params?: GetCustomerParams): Promise<StripeCustomerResponse | null> {
  const loggedIn = localStorage.getItem('accessToken');

  // WHY null return for unauthenticated:
  // Customer data is user-specific, null allows graceful handling
  // in components without error boundaries
  if (!loggedIn) return null;
  const config = {
    method: 'get' as const,
    path: 'stripe/customer',
  };

  const data = await getData<StripeCustomerResponse>({ ...config });

  return data;
}

/**
 * Updates Stripe customer details.
 *
 * WHY customer update needed:
 * - Update billing address for tax calculation
 * - Change default payment method
 * - Update email for receipts
 * - Update phone for SMS notifications
 *
 * Security considerations:
 * - Backend validates customer belongs to authenticated user
 * - Customer template prevents unexpected field injection
 * - Address validation for tax compliance
 *
 * @param {UpdateCustomerParams} customerDetails - Customer update details
 * @returns {Promise<StripeCustomerResponse>} Updated customer object
 * @security Backend validates ownership and sanitizes input
 */
export async function updateCustomer(customerDetails: UpdateCustomerParams): Promise<StripeCustomerResponse> {
  // WHY: Use explicit name if provided (e.g., from Stripe AddressElement in BillingEdit),
  // otherwise fall back to constructing from given_name + family_name (Cognito attrs).
  // DEV-1907: Previous code always used given_name+family_name, silently discarding
  // the billing name the user typed into the AddressElement.
  const billingName = customerDetails.name ||
    ((customerDetails.given_name || '') + ' ' + (customerDetails.family_name || '')).trim() ||
    undefined;

  const customerPayload = {
    name: billingName,
    address: { ...customerDetails.address },
    email: customerDetails.email,
    phone: customerDetails.phone,
  };

  const config = {
    method: 'patch' as const,
    path: `stripe/customer`,
    body: customerPayload,
  };
  const data = await getData<StripeCustomerResponse>({
    ...config,
  });

  return data;
}

/**
 * Confirms and captures a payment after successful authentication.
 *
 * WHY separate confirmation from creation:
 * - Supports asynchronous authentication flows (3D Secure)
 * - Allows user to review final amount before charging
 * - Handles bank authentication requirements (PSD2)
 * - Prevents accidental double charges
 *
 * Security critical points:
 * - Backend validates payment belongs to authenticated user
 * - Amount locked at creation time (cannot be modified)
 * - Idempotency ensures single charge even with retries
 * - Webhook confirms final status (authoritative source)
 *
 * Confirmation flow:
 * 1. PaymentIntent created with amount
 * 2. User completes authentication (if required)
 * 3. Frontend calls this to confirm
 * 4. Backend captures the payment
 * 5. Webhook updates final status
 *
 * @param {ConfirmPaymentParams} params - Confirmation parameters
 * @param {string} params.paymentIntentID - Stripe PaymentIntent ID to confirm
 * @returns {Promise<PaymentIntentResponse>} Payment confirmation with final status
 * @security CRITICAL: Backend must verify ownership and amount integrity
 */
export async function confirmPayment({ paymentIntentID }: ConfirmPaymentParams): Promise<PaymentIntentResponse> {
  const payload = {
    paymentIntentID, // WHY only ID needed: Amount already locked in PaymentIntent
  };

  const config = {
    method: 'post' as const,
    path: `stripe/confirm-payment`,
    body: payload,
  };

  const data = await getData<PaymentIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Cancels Stripe subscription.
 *
 * WHY cancel subscription:
 * - User requested cancellation
 * - Payment method expired and user doesn't want to update
 * - Downgrade to free plan
 * - Account closure
 *
 * Cancellation options:
 * - Immediate: Cancel subscription now (refund pro-rated amount)
 * - At period end: Continue until current billing period ends (default)
 *
 * @param {CancelStripeSubscriptionParams} subscriptionData - Subscription cancellation details
 * @returns {Promise<unknown>} Cancellation confirmation
 * @security Backend validates user owns subscription before cancelling
 */
export async function cancelSubscription(subscriptionData: CancelStripeSubscriptionParams): Promise<unknown> {
  const payload = {
    ...subscriptionData,
  };

  const config = {
    method: 'post' as const,
    path: `stripe/cancel-subscription`,
    body: payload,
  };

  const data = await getData({
    ...config,
  });

  return data;
}

/**
 * Reconciles payment data with Stripe (source of truth).
 *
 * WHY payment reconciliation critical:
 * - Webhook delivery failures can cause data inconsistency
 * - Manual admin corrections in Stripe dashboard
 * - Verify payment status after network issues
 * - Ensure database matches Stripe's authoritative records
 *
 * Security considerations:
 * - Backend verifies payment belongs to authenticated user
 * - Fetches latest status from Stripe API
 * - Updates local database to match Stripe
 * - Audit logged for compliance
 *
 * Use cases:
 * - Failed webhook delivery (status mismatch)
 * - Manual refund processed in Stripe dashboard
 * - Payment dispute resolution
 * - Customer support investigation
 *
 * @param {ReconPaymentParams} params - Reconciliation parameters
 * @param {string} params.paymentId - Local payment ID to reconcile
 * @returns {Promise<ReconciliationResponse>} Reconciliation result with updated status
 * @security CRITICAL: Ensures data integrity between local DB and Stripe
 */
export async function reconPayment({ paymentId }: ReconPaymentParams): Promise<ReconciliationResponse> {
  const payload = {
    paymentID: paymentId,
  };

  const config = {
    method: 'post' as const,
    path: `stripe/recon-payment`,
    body: payload,
  };

  const data = await getData<ReconciliationResponse>({
    ...config,
  });

  return data;
}

/**
 * Lists all payment methods for the authenticated user with usage information.
 *
 * WHY separate endpoint from getCustomer():
 * - Payment method management requires detailed usage information (subscriptions, billings)
 * - Backend aggregates which subscriptions/billings use each payment method
 * - Includes currency, stripe_customer_id for multi-currency deduplication
 * - Calculates is_expired and can_remove flags server-side
 * - Optimized for payment method CRUD operations vs customer data retrieval
 *
 * Response structure:
 * - payment_methods array with card details
 * - subscriptions_using array (subscription IDs using this payment method)
 * - billings_using array (billing IDs using this payment method)
 * - is_expired boolean (true if card expired)
 * - can_remove boolean (true if removal allowed based on active subscriptions/billings)
 * - currency string (gbp, eur, usd)
 * - stripe_customer_id string (for tracking across currencies)
 *
 * Security:
 * - Backend filters to authenticated user's payment methods only
 * - No sensitive card data (only last 4 digits, brand, expiry)
 * - Usage information helps prevent accidental removal of active payment methods
 *
 * @param {ListPaymentMethodsParams} _params - No parameters (uses session)
 * @returns {Promise<PaymentMethodResponse[]>} Payment methods list with usage metadata
 * @security Backend ensures user can only see their own payment methods
 * @see {@link docs/PAYMENT_METHOD_MANAGEMENT_OVERVIEW.md} - Complete payment method architecture
 */
export async function listPaymentMethods(_params?: ListPaymentMethodsParams): Promise<PaymentMethodResponse[]> {
  const config = {
    method: 'get' as const,
    path: 'payment-methods', // Backend expects payment-methods without stripe/ prefix
  };

  const data = await getData<PaymentMethodResponse[]>({
    ...config,
  });

  return data;
}

/**
 * Removes (detaches) a payment method from user's account.
 *
 * WHY server-side validation required:
 * - Cannot remove last active payment method if user has active subscriptions/billings
 * - Backend validates payment method belongs to authenticated user
 * - Prevents removal of payment methods currently in use
 * - Audit trail for compliance and dispute resolution
 *
 * Security critical points:
 * - Backend must verify ownership before removal
 * - Cannot remove if it would violate "at least one active method" constraint
 * - Transaction-safe: If removal fails, no changes persist
 * - Audit logged for fraud prevention and customer support
 *
 * Error scenarios:
 * - Last active payment method with active subscriptions → 400 error
 * - Payment method doesn't belong to user → 403 error
 * - Payment method in use by active subscription → 400 error
 * - Already removed → 404 error
 *
 * @param {RemovePaymentMethodParams} params - Removal parameters
 * @param {string} params.paymentMethodId - Stripe payment method ID (pm_xxx)
 * @returns {Promise<unknown>} Confirmation of removal
 * @security CRITICAL: Backend validates constraint before removal
 */
export async function removePaymentMethod({
  paymentMethodId,
}: RemovePaymentMethodParams): Promise<unknown> {
  const config = {
    method: 'delete' as const,
    path: `payment-methods/${paymentMethodId}`, // Backend expects payment-methods without stripe/ prefix
  };

  const data = await getData({
    ...config,
  });

  return data;
}

/**
 * Updates payment method for one or more subscriptions.
 *
 * WHY bulk update needed:
 * - User may want to update multiple subscriptions at once
 * - Efficient single API call vs multiple sequential calls
 * - Transactional: Either all succeed or all fail (atomic operation)
 * - Reduces risk of partial updates leaving inconsistent state
 *
 * Security architecture:
 * - Backend verifies all subscriptions belong to authenticated user
 * - Backend validates payment method belongs to user
 * - Backend checks payment method currency matches subscription currency
 * - Backend ensures payment method is active (not expired)
 *
 * Use cases:
 * - User updates payment method from subscription management page
 * - Payment recovery flow after subscription payment failure
 * - Proactive payment method update before expiry
 * - Switching from expired card to new card
 *
 * @param {UpdateSubscriptionPaymentMethodParams} params - Update parameters
 * @param {string[]} params.subscriptionIds - Array of Stripe subscription IDs (sub_xxx)
 * @param {string} params.paymentMethodId - Stripe payment method ID (pm_xxx)
 * @returns {Promise<unknown>} Confirmation with updated subscription details
 * @security Backend validates ownership and currency compatibility
 */
export async function updateSubscriptionPaymentMethod({
  subscriptionIds,
  paymentMethodId,
}: UpdateSubscriptionPaymentMethodParams): Promise<unknown> {
  // WHY snake_case for backend API:
  // Backend Lambda expects snake_case field names (payment_method_id, subscription_ids)
  // Frontend uses camelCase TypeScript conventions, so we transform here
  const config = {
    method: 'put' as const,
    path: 'payment-methods/subscription', // Backend expects payment-methods without stripe/ prefix
    body: {
      subscription_ids: subscriptionIds, // WHY array: Supports bulk updates
      payment_method_id: paymentMethodId, // WHY snake_case: Backend Lambda expects this format
    },
  };

  const data = await getData({
    ...config,
  });

  return data;
}

/**
 * Updates payment method for a specific billing item.
 *
 * WHY separate from subscription update:
 * - Billing items may be one-time payments vs recurring subscriptions
 * - Different backend logic for billing vs subscription updates
 * - Billing may have different currency/customer context
 * - Allows granular control over individual billing items
 *
 * Security architecture:
 * - Backend verifies billing belongs to authenticated user
 * - Backend validates payment method belongs to user
 * - Backend checks currency compatibility
 * - Backend ensures payment method is active
 *
 * Use cases:
 * - User updates payment method for failed billing from Billing History
 * - Payment recovery for specific unpaid invoice
 * - Changing payment method for pending billing item
 *
 * @param {UpdateBillingPaymentMethodParams} params - Update parameters
 * @param {string} params.billingId - Stripe PaymentIntent or Invoice ID
 * @param {string} params.paymentMethodId - Stripe payment method ID (pm_xxx)
 * @returns {Promise<unknown>} Confirmation with updated billing details
 * @security Backend validates ownership and currency compatibility
 */
export async function updateBillingPaymentMethod({
  billingId,
  paymentMethodId,
}: UpdateBillingPaymentMethodParams): Promise<unknown> {
  const config = {
    method: 'put' as const,
    path: 'stripe/billings/payment-method',
    body: {
      billingId,
      paymentMethodId,
    },
  };

  const data = await getData({
    ...config,
  });

  return data;
}

/**
 * Retrieves PaymentIntent details for payment recovery flows.
 *
 * WHY payment recovery endpoint needed:
 * - When subscription payment fails (past_due, unpaid), need recovery details
 * - Backend provides failure reason, payment intent status, retry options
 * - Supports 3D Secure authentication re-attempt
 * - Returns client_secret for Stripe.js payment confirmation
 *
 * Security architecture:
 * - Backend verifies subscription/payment belongs to authenticated user
 * - Client secret single-use and expires after 24 hours
 * - No sensitive payment details exposed (only status and retry options)
 * - Audit logged for fraud prevention
 *
 * Use cases:
 * - Subscription payment failed due to expired card
 * - Subscription payment failed due to insufficient funds
 * - 3D Secure authentication required but not completed
 * - User wants to retry payment with updated payment method
 *
 * Response structure:
 * - payment_intent object with status, failure reason
 * - client_secret for Stripe.js confirmation
 * - retry_options with available actions
 * - subscription details for context
 *
 * @param {RetrievePaymentIntentParams} params - Recovery parameters
 * @param {string} [params.paymentIntentId] - Stripe PaymentIntent ID (pi_xxx)
 * @param {string} [params.subscriptionId] - Stripe Subscription ID (sub_xxx)
 * @returns {Promise<PaymentIntentResponse>} Payment intent with recovery options
 * @security Backend validates ownership before returning recovery details
 */
export async function retrievePaymentIntent({
  paymentIntentId,
  subscriptionId,
}: RetrievePaymentIntentParams): Promise<PaymentIntentResponse> {
  const config = {
    method: 'post' as const,
    path: 'payment-methods/recover', // Backend expects payment-methods without stripe/ prefix
    body: {
      paymentIntentId, // WHY both IDs: Support recovery by either identifier
      subscriptionId,
    },
  };

  const data = await getData<PaymentIntentResponse>({
    ...config,
  });

  return data;
}

/**
 * Parameters for fetching invoice URL
 *
 * WHY both identifiers:
 * - invoiceId: Used when we have the direct invoice ID (e.g., from transformed data)
 * - subscriptionId: Used when we only have subscription_id (raw ffe_payments data)
 *   Lambda will look up the latest paid invoice for the subscription
 */
interface GetInvoiceUrlParams {
  invoiceId?: string | null;
  subscriptionId?: string | null;
  paymentId?: string | null;
}

/**
 * Retrieves the hosted invoice URL for a specific invoice.
 *
 * WHY on-demand fetching:
 * - Reduces overhead of fetching URLs for all payments in history list
 * - Only fetches when user explicitly requests to view invoice
 * - Uses dedicated lambda to keep ffePaymentHistory lightweight
 *
 * @param {GetInvoiceUrlParams} params - Object containing invoiceId and/or subscriptionId
 * @returns {Promise<{ invoiceUrl: string }>} Object containing the invoice URL
 */
export async function getInvoiceUrl(params: GetInvoiceUrlParams): Promise<{ invoiceUrl: string }> {
  const { invoiceId, subscriptionId, paymentId } = params;

  // Build request body with only defined values
  const body: Record<string, string> = {};
  if (invoiceId) body.invoiceId = invoiceId;
  if (subscriptionId) body.subscriptionId = subscriptionId;
  if (paymentId) body.paymentId = paymentId;

  const config = {
    method: 'post' as const,
    path: 'stripe/invoice-url', // Maps to ffeGetStripeInvoiceUrl
    body,
  };

  const data = await getData<{ invoiceUrl: string }>({
    ...config,
  });

  return data;
}

/**
 * Auto-renew response from PUT /subscriptions/{id}/auto-renew
 *
 * WHY inline type: This response shape is unique to this endpoint and not
 * reused elsewhere. Adding it to the shared types barrel would be noise.
 *
 * Two response shapes:
 * - Case 1 (standard toggle): No `type` field, just updated subscription state
 * - Case 2 (re-subscribe expired sub): `type: 'new_subscription'` with potential
 *   3DS or payment method failure fields. The backend silently creates a brand new
 *   Stripe subscription when toggling auto-renew ON for a canceled sub.
 */
export interface UpdateAutoRenewResponse {
  subscription_id: string;
  auto_renew: boolean;
  cancel_at_period_end: boolean;
  status: string;
  current_period_end: string | null;
  // Case 2 fields -- only present when backend creates a new subscription
  type?: 'new_subscription';
  requires_action?: boolean;       // 3DS authentication needed
  requires_payment_method?: boolean; // Card declined / expired
  client_secret?: string;          // For stripe.confirmCardPayment (3DS or retry)
  payment_intent_id?: string;      // Stripe PaymentIntent ID
  message?: string;                // User-facing error from backend (card declined)
}

/**
 * Toggles auto-renew (Stripe's cancel_at_period_end) on a subscription.
 *
 * WHY separate endpoint from cancelSubscription:
 * - cancelSubscription is a hard cancel (immediate or at period end, irreversible intent)
 * - updateAutoRenew is a reversible toggle: user can re-enable before period ends
 * - Backend uses Stripe's subscriptions.update({ cancel_at_period_end }) under the hood
 * - No payment is triggered in either direction
 *
 * Error codes from backend:
 * - MISSING_PARAMETERS (400): Missing subscriptionId or enabled boolean
 * - UNAUTHORIZED (401): Subscription doesn't belong to this user
 * - SUBSCRIPTION_NOT_FOUND (404): Subscription ID not found in Stripe
 * - SUBSCRIPTION_NOT_ACTIVE (409): Subscription in terminal state (canceled/expired)
 * - UPDATE_AUTO_RENEW_FAILED (500): Stripe API error
 *
 * @param {UpdateAutoRenewParams} params - Subscription ID and desired enabled state
 * @returns {Promise<UpdateAutoRenewResponse>} Updated subscription state
 * @security Backend validates user owns subscription before toggling
 */
export async function updateAutoRenew({
  subscriptionId,
  enabled,
}: UpdateAutoRenewParams): Promise<UpdateAutoRenewResponse> {
  const config = {
    method: 'put' as const,
    path: `subscriptions/${subscriptionId}/auto-renew`,
    body: { enabled },
  };

  const data = await getData<UpdateAutoRenewResponse>({
    ...config,
  });

  return data;
}
